import { KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PHONE, useMediaQuery } from '../shared/media';
import { Command, inEditable, matchCommands } from '../shared/commandIndex';

/**
 * The ⌘K jump box (docs/PLAYBOOK-redesign.md §4, §7 R1).
 *
 * Opened by Ctrl/⌘+K from anywhere in the shell (never from inside an input,
 * textarea, contenteditable or the builder's inline editors), by the search
 * field in the top bar, or by the search icon in the phone bar. It searches
 * the client-side index the layout hands it, plus whatever `more` resolves
 * to once it is open (the forms list, fetched lazily). ↑/↓ move, Enter goes,
 * Esc closes — and Esc closes ONLY this: the window listener runs in the
 * capture phase and stops the event there, so `unsaved.tsx`, the builder and
 * any Modal underneath never see it.
 *
 * On a phone it is a full-height sheet with a visible ✕ (CLAUDE.md rule 5).
 */
export function CommandPalette({ open, onClose, commands, more, title }: {
  open: boolean;
  onClose: () => void;
  /** What the palette offers before anything is fetched. */
  commands: Command[];
  /** Fetched once per opening: the forms list, or anything else that costs a request. */
  more?: () => Promise<Command[]>;
  /** The dialog's accessible name; defaults to "Jump to". */
  title?: string;
}) {
  const navigate = useNavigate();
  const phone = useMediaQuery(PHONE);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState(0);
  const [extra, setExtra] = useState<Command[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Fresh each time: the query, the cursor, and the lazily fetched rows.
  useEffect(() => {
    if (!open) return;
    setQuery(''); setAt(0); setExtra([]);
    let live = true;
    if (more) more().then((rows) => { if (live) setExtra(rows); }).catch(() => undefined);
    // The input mounts with the palette; focus it once it is there.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => { live = false; clearTimeout(t); };
  }, [open, more]);

  // Esc closes the palette and nothing else (capture phase, stopped there).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault(); e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const all = useMemo(() => [...commands, ...extra], [commands, extra]);
  const rows = useMemo(() => matchCommands(all, query), [all, query]);
  useEffect(() => { setAt(0); }, [query, rows.length]);

  const go = (c: Command) => {
    onClose();
    if (c.run) c.run();
    else if (c.to) navigate(c.to);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => Math.min(rows.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (rows[at]) go(rows[at]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
  };

  // Keep the highlighted row in view as the arrows move it.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('.fs-cmd-item.on');
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [at]);

  if (!open) return null;
  return (
    <>
      <div className="fs-cmd-scrim" onClick={onClose} />
      <div className={`fs-cmd${phone ? ' sheet' : ''}`} role="dialog" aria-modal="true" aria-label={title || 'Jump to'}>
        <div className="fs-cmd-head">
          <input
            ref={inputRef} className="fs-cmd-input" type="text" value={query}
            placeholder="Jump to a form, page or setting…" aria-label="Jump to a form, page or setting"
            autoComplete="off" spellCheck={false}
            onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
          />
          <button type="button" className="fs-btn icon fs-cmd-close" title="Close" onClick={onClose}>✕</button>
        </div>
        <div className="fs-cmd-list" ref={listRef} role="listbox" aria-label="Results">
          {rows.length === 0 && <div className="fs-cmd-empty">Nothing matches “{query}”.</div>}
          {rows.map((c, i) => (
            <button
              key={c.id} type="button" role="option" aria-selected={i === at}
              className={`fs-cmd-item${i === at ? ' on' : ''}${c.danger ? ' danger' : ''}`}
              onMouseEnter={() => setAt(i)} onClick={() => go(c)}
            >
              <span className="k">{c.group}</span>
              <span className="l">{c.label}{c.hint && <span className="h"> {c.hint}</span>}</span>
              <span className="ret" aria-hidden="true">↵</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * The shell's global shortcuts: Ctrl/⌘+K opens the palette, Ctrl/⌘+Shift+L
 * logs out (Appendix B). Neither fires from inside something a person types
 * in. Ctrl+K is Firefox's own "focus the search bar", so it is prevented
 * here — the page's box is the one that opens.
 */
export function useShellShortcuts({ onPalette, onLogout }: { onPalette: () => void; onLogout: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'k' && !e.shiftKey) {
        if (inEditable(e.target)) return;
        e.preventDefault();
        onPalette();
      } else if (k === 'l' && e.shiftKey) {
        if (inEditable(e.target)) return;
        e.preventDefault();
        onLogout();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onPalette, onLogout]);
}

/** The magnifier the search box and the phone bar's search button draw. */
export function SearchGlyph({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5 14 14" />
    </svg>
  );
}

/** Two initials for a pill or an avatar: "Acme Field Services" → "AF", "dana@x" → "DA". */
export function initialsOf(name?: string | null, fallback?: string | null): string {
  const src = (name && name.trim()) || (fallback && fallback.split('@')[0]) || '';
  const words = src.replace(/[^\p{L}\p{N} ]+/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const s = words.length >= 2 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return s.toUpperCase();
}
